import { readOrgFile, writeOrgFile } from '../storage/FileSystemAdapter';
import { appendChangeLog } from './changeLog';

// ─── BIDIRECTIONAL REFERENCE SYNC ────────────────────────────────────────────
//
// Generic helper that keeps the reverse pointers on the "other side" of a
// bidirectional reference coherent with the forward pointers persisted on
// the entity that was just saved.
//
// Best-effort contract: the primary writeOrgFile of the saved entity is the
// source of truth and MUST complete first. This helper runs afterwards and
// processes only the delta between previousOtherIds and currentOtherIds.
// Other-side records that no longer exist on disk are silently skipped;
// records flagged immutable by isOtherImmutable are also skipped (and the
// skip is logged to console). Drift in pre-existing records is not
// retroactively cleaned — only the delta of the current save is synced.
//
// Returns { updated, skipped, failed } string arrays. The caller is free to
// ignore the return value.
export async function syncBidirectionalRefs({
  orgId,
  entityId,
  otherCollection,
  otherIdsField,
  previousOtherIds,
  currentOtherIds,
  changedBy,
  isOtherImmutable,
}) {
  const prev = Array.isArray(previousOtherIds) ? previousOtherIds : [];
  const curr = Array.isArray(currentOtherIds)  ? currentOtherIds  : [];
  const prevSet = new Set(prev);
  const currSet = new Set(curr);
  const toAdd    = curr.filter(id => !prevSet.has(id));
  const toRemove = prev.filter(id => !currSet.has(id));

  const updated = [];
  const skipped = [];
  const failed  = [];

  const processOne = async (otherId, op) => {
    let other;
    try {
      other = await readOrgFile(orgId, `${otherCollection}/${otherId}.json`);
    } catch {
      skipped.push(otherId);
      return;
    }
    if (typeof isOtherImmutable === 'function' && isOtherImmutable(other)) {
      console.warn(
        `[relationSync] skipped immutable record ${otherCollection}/${otherId}.json — ` +
        `${otherIdsField} not updated (op=${op}, peer=${entityId})`
      );
      skipped.push(otherId);
      return;
    }
    const before = Array.isArray(other[otherIdsField]) ? other[otherIdsField] : [];
    let after;
    if (op === 'add') {
      if (before.includes(entityId)) return;
      after = [...before, entityId];
    } else {
      if (!before.includes(entityId)) return;
      after = before.filter(x => x !== entityId);
    }
    const now = new Date().toISOString();
    const next = { ...other, [otherIdsField]: after, updatedAt: now };
    next.changeLog = appendChangeLog(other, next, changedBy);
    try {
      await writeOrgFile(orgId, `${otherCollection}/${otherId}.json`, next);
      updated.push(otherId);
    } catch {
      failed.push(otherId);
    }
  };

  for (const id of toAdd)    await processOne(id, 'add');
  for (const id of toRemove) await processOne(id, 'remove');

  return { updated, skipped, failed };
}
